import clsx from "clsx"
import { type ReactNode, useState } from "react"

import { Avatar, TypeIcon } from "@/components/Standup"
import type { WorkItem, WorkItemType } from "@/types/sprint-report"

interface Props {
  items: WorkItem[]
}

/**
 * Scope for this tab: open (non-Done) **Bugs at P1 or P2**, plus **PBIs and
 * Project Items at P1 only**. Exported so the tab's chip counts (App.tsx) use
 * the exact same rule.
 */
export function inP1P2Scope(it: WorkItem): boolean {
  if (it.state_category === "done") return false
  const p = it.priority.major
  if (it.work_item_type === "Bug") return p === "P1" || p === "P2"
  if (it.work_item_type === "Product Backlog Item") return p === "P1"
  if (it.work_item_type === "Project Item") return p === "P1"
  return false
}

// Explicit section order: all the P1 tables first (Bugs, then PBIs, then Project
// Items), with Bug P2 last.
const SECTION_ORDER: { type: WorkItemType; label: string; priority: "P1" | "P2" }[] = [
  { type: "Bug", label: "Bugs", priority: "P1" },
  { type: "Product Backlog Item", label: "Product Backlog Items", priority: "P1" },
  { type: "Project Item", label: "Project Items", priority: "P1" },
  { type: "Bug", label: "Bugs", priority: "P2" },
]

const MINOR_RANK: Record<string, number> = { A: 0, B: 1 }
const MAJOR_RANK: Record<string, number> = { P0: 0, P1: 1, P2: 2, P3: 3 }

/* --------------------------------------------------------------- colour */

const PRI_HEX: Record<string, string> = {
  P0: "#C00000",
  P1: "#E97132",
  P2: "#BF8F00",
  P3: "#595959",
}
const PRI_BG: Record<string, string> = {
  P0: "bg-priority-p0",
  P1: "bg-priority-p1",
  P2: "bg-priority-p2",
  P3: "bg-priority-p3",
}

type Tone = "pre" | "dev" | "devdone" | "qa" | "done" | "other"

function categoryTone(cat?: string): Tone {
  switch (cat) {
    case "pre_dev":
      return "pre"
    case "in_dev":
      return "dev"
    case "dev_complete":
      return "devdone"
    case "in_qa":
      return "qa"
    case "done":
      return "done"
    default:
      return "other"
  }
}

/** Tone from a free-text state (bundle headers carry only a state string). */
function stateStringTone(s: string): Tone {
  const t = s.trim().toLowerCase()
  if (t.includes("dev") && t.includes("done")) return "devdone"
  if (t === "done" || t === "closed") return "done"
  if (t.includes("qa")) return "qa"
  if (
    t.includes("dev") ||
    t.includes("committed") ||
    t.includes("wip") ||
    t.includes("pr") ||
    t.includes("progress")
  )
    return "dev"
  return "pre"
}

const PILL: Record<Tone, string> = {
  pre: "bg-slate-100 text-slate-600 ring-slate-200",
  dev: "bg-sky-100 text-sky-800 ring-sky-200",
  devdone: "bg-teal-100 text-teal-700 ring-teal-300",
  qa: "bg-amber-100 text-amber-900 ring-amber-300",
  done: "bg-emerald-100 text-emerald-700 ring-emerald-300",
  other: "bg-slate-100 text-slate-600 ring-slate-200",
}
const RAIL: Record<Tone, string> = {
  pre: "bg-slate-300",
  dev: "bg-sky-500",
  devdone: "bg-teal-500",
  qa: "bg-amber-400",
  done: "bg-emerald-500",
  other: "bg-slate-300",
}

function PriChip({ raw, major }: { raw: string; major: string }) {
  return (
    <span
      className={clsx(
        "inline-block shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold leading-none text-white",
        PRI_BG[major] ?? "bg-slate-400",
      )}
    >
      {raw}
    </span>
  )
}

function StatePill({ state, category }: { state: string; category?: string }) {
  const tone = category ? categoryTone(category) : stateStringTone(state)
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1",
        PILL[tone],
      )}
    >
      {state}
    </span>
  )
}

/* ----------------------------------------------------------------- sort */

type SortCol = "id" | "priority" | "state" | "sprint" | "assignee" | "title"
interface SortKey {
  col: SortCol
  dir: "asc" | "desc"
}

const DEFAULT_SORT: SortKey[] = [
  { col: "priority", dir: "asc" },
  { col: "state", dir: "asc" },
  { col: "id", dir: "asc" },
]

const COLUMNS: { col: SortCol; label: string }[] = [
  { col: "id", label: "#" },
  { col: "priority", label: "Priority" },
  { col: "state", label: "State" },
  { col: "sprint", label: "Sprint" },
  { col: "assignee", label: "Assignee" },
  { col: "title", label: "Title" },
]

function sprintLabel(item: WorkItem): string {
  const s = item.sprint
  if (!s) return "Backlog"
  if (s.is_current) return s.number != null ? `S${s.number} (current)` : "Current"
  if (s.is_next) return s.number != null ? `S${s.number} (next)` : "Next"
  if (s.number != null) return `S${s.number}`
  return "—"
}

function sprintRank(it: WorkItem): number {
  return it.sprint?.number ?? 9999
}

function compareBy(a: WorkItem, b: WorkItem, col: SortCol): number {
  switch (col) {
    case "id":
      return a.id - b.id
    case "priority": {
      const maj = (MAJOR_RANK[a.priority.major] ?? 9) - (MAJOR_RANK[b.priority.major] ?? 9)
      if (maj !== 0) return maj
      return (MINOR_RANK[a.priority.minor ?? ""] ?? 2) - (MINOR_RANK[b.priority.minor ?? ""] ?? 2)
    }
    case "state":
      return a.state_order - b.state_order
    case "sprint":
      return sprintRank(a) - sprintRank(b)
    case "assignee":
      return (a.assigned_to?.name ?? "~~~").localeCompare(b.assigned_to?.name ?? "~~~")
    case "title":
      return a.title.localeCompare(b.title)
  }
}

function multiSort(items: WorkItem[], keys: SortKey[]): WorkItem[] {
  return [...items].sort((a, b) => {
    for (const k of keys) {
      const c = compareBy(a, b, k.col)
      if (c !== 0) return k.dir === "asc" ? c : -c
    }
    return a.id - b.id
  })
}

/* ----------------------------------------------------------- shared table */

interface TableProps {
  rows: WorkItem[]
  sort: SortKey[]
  onSort: (col: SortCol, additive: boolean) => void
  /** Bundle view shows the assignee once in the header instead of per row. */
  hideAssignee?: boolean
}

function ItemTable({ rows, sort, onSort, hideAssignee }: TableProps) {
  const columns = hideAssignee ? COLUMNS.filter((c) => c.col !== "assignee") : COLUMNS
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm">
      <table className="w-full text-xs">
        <thead>
          <tr>
            {columns.map((c) => {
              const idx = sort.findIndex((k) => k.col === c.col)
              const key = idx >= 0 ? sort[idx] : null
              return (
                <th
                  key={c.col}
                  onClick={(e) => onSort(c.col, e.shiftKey)}
                  className={clsx(
                    "cursor-pointer select-none whitespace-nowrap border-b border-slate-200 bg-slate-50/80 px-2.5 py-2 text-left text-[10px] font-semibold uppercase tracking-wider transition-colors hover:bg-slate-100",
                    key ? "text-ink" : "text-slate-400",
                  )}
                  title="Click to sort · Shift-click to add a secondary sort"
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {key && (
                      <span className="text-[9px] text-ink/70">
                        {key.dir === "asc" ? "▲" : "▼"}
                        {sort.length > 1 && <sup className="ml-0.5">{idx + 1}</sup>}
                      </span>
                    )}
                  </span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((it) => (
            <tr key={it.id} className="border-t border-slate-100 transition-colors hover:bg-slate-50/80">
              <td className="whitespace-nowrap px-2.5 py-1.5">
                <a
                  href={it.url}
                  target="_blank"
                  rel="noreferrer"
                  className="num text-[11px] text-slate-400 hover:text-blue-700 hover:underline"
                >
                  {it.id}
                </a>
              </td>
              <td className="whitespace-nowrap px-2.5 py-1.5">
                <PriChip raw={it.priority.raw} major={it.priority.major} />
              </td>
              <td className="whitespace-nowrap px-2.5 py-1.5">
                <StatePill state={it.state} category={it.state_category} />
              </td>
              <td className="whitespace-nowrap px-2.5 py-1.5">
                <span
                  className={clsx(
                    "text-[11px]",
                    it.sprint?.is_current ? "font-medium text-ink" : "text-slate-500",
                  )}
                >
                  {sprintLabel(it)}
                </span>
              </td>
              {!hideAssignee && (
                <td
                  className={clsx(
                    "whitespace-nowrap px-2.5 py-1.5 text-[11px]",
                    it.assigned_to ? "text-slate-600" : "italic text-slate-400",
                  )}
                >
                  {it.assigned_to?.name ?? "Unassigned"}
                </td>
              )}
              <td className="px-2.5 py-1.5">
                <a
                  href={it.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[13px] text-slate-700 hover:text-ink hover:underline"
                >
                  {it.title}
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ------------------------------------------------------------------ views */

interface TypeSection {
  key: string
  type: WorkItemType
  label: string
  priority: "P1" | "P2"
  rows: WorkItem[]
}

function byTypeSections(scoped: WorkItem[], sort: SortKey[]): TypeSection[] {
  return SECTION_ORDER.map(({ type, label, priority }) => ({
    key: `${type}-${priority}`,
    type,
    label,
    priority,
    rows: multiSort(
      scoped.filter((it) => it.work_item_type === type && it.priority.major === priority),
      sort,
    ),
  })).filter((s) => s.rows.length > 0)
}

interface BundleSection {
  key: string
  code: string
  title: string
  state: string
  /** Distinct assignees across the bundle's items, shown as header chips. */
  assignees: string[]
  rows: WorkItem[]
}

function byBundleSections(scoped: WorkItem[], sort: SortKey[]) {
  const byBundle = new Map<number, WorkItem[]>()
  const loose: WorkItem[] = []
  for (const it of scoped) {
    if (it.bundle) {
      const id = it.bundle.id ?? -1
      const arr = byBundle.get(id) ?? []
      arr.push(it)
      byBundle.set(id, arr)
    } else {
      loose.push(it)
    }
  }

  const bundleSections: BundleSection[] = [...byBundle.entries()]
    .map(([id, rows]) => {
      const b = rows[0].bundle!
      const assignees = [
        ...new Set(rows.map((r) => r.assigned_to?.name).filter((n): n is string => !!n)),
      ].sort()
      return {
        key: `bundle-${id}`,
        code: b.code ?? "",
        title: b.title ?? "Bundle",
        state: b.state ?? "",
        assignees,
        rows: multiSort(rows, sort),
      }
    })
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))

  return { bundleSections, looseSections: byTypeSections(loose, sort) }
}

/* ------------------------------------------------------------------ root */

type View = "type" | "bundle"

export function P1Items({ items }: Props) {
  const [sort, setSort] = useState<SortKey[]>(DEFAULT_SORT)
  const [view, setView] = useState<View>("type")

  const scoped = items.filter(inP1P2Scope)

  // Plain click → sort by this column alone (toggle direction if already primary).
  // Shift-click → add it as an additional sort key (toggle direction if present).
  const onSort = (col: SortCol, additive: boolean) => {
    setSort((prev) => {
      const idx = prev.findIndex((k) => k.col === col)
      if (additive) {
        if (idx === -1) return [...prev, { col, dir: "asc" }]
        const next = [...prev]
        next[idx] = { col, dir: next[idx].dir === "asc" ? "desc" : "asc" }
        return next
      }
      if (idx === 0) return [{ col, dir: prev[0].dir === "asc" ? "desc" : "asc" }]
      return [{ col, dir: "asc" }]
    })
  }

  if (scoped.length === 0) {
    return (
      <p className="rounded border border-slate-300 p-6 text-center text-sm text-slate-500">
        No open P1/P2 items in scope.
      </p>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] text-slate-500">
          Bugs <strong className="text-priority-p1">P1</strong> +{" "}
          <strong className="text-priority-p2">P2</strong> · PBIs and Project Items{" "}
          <strong className="text-priority-p1">P1</strong> · open only. Click a column to sort;{" "}
          <strong>⇧-click</strong> to add a secondary sort.
        </p>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {view === "type" ? (
        <div className="space-y-6">
          {byTypeSections(scoped, sort).map((s) => (
            <section key={s.key} className="animate-rise">
              <TypeSectionHeader section={s} />
              <ItemTable rows={s.rows} sort={sort} onSort={onSort} />
            </section>
          ))}
        </div>
      ) : (
        <BundleView scoped={scoped} sort={sort} onSort={onSort} />
      )}
    </div>
  )
}

function BundleView({
  scoped,
  sort,
  onSort,
}: {
  scoped: WorkItem[]
  sort: SortKey[]
  onSort: (col: SortCol, additive: boolean) => void
}) {
  const { bundleSections, looseSections } = byBundleSections(scoped, sort)
  return (
    <div className="space-y-7">
      {bundleSections.length > 0 && (
        <div className="space-y-5">
          <GroupLabel>Bundles · {bundleSections.length}</GroupLabel>
          {bundleSections.map((s) => (
            <section key={s.key} className="animate-rise">
              <BundleSectionHeader section={s} />
              <ItemTable rows={s.rows} sort={sort} onSort={onSort} hideAssignee />
            </section>
          ))}
        </div>
      )}
      {looseSections.length > 0 && (
        <div className="space-y-5">
          <GroupLabel>Not in a bundle</GroupLabel>
          {looseSections.map((s) => (
            <section key={s.key} className="animate-rise">
              <TypeSectionHeader section={s} />
              <ItemTable rows={s.rows} sort={sort} onSort={onSort} />
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

/* ----------------------------------------------------------------- atoms */

function TypeSectionHeader({ section }: { section: TypeSection }) {
  return (
    <div className="mb-1.5 flex items-center gap-2">
      <span
        className="h-4 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: PRI_HEX[section.priority] }}
      />
      <TypeIcon type={section.type} />
      <h2 className="font-display text-sm font-bold text-ink">{section.label}</h2>
      <PriChip raw={section.priority} major={section.priority} />
      <span className="num text-[11px] font-medium text-slate-400">{section.rows.length}</span>
    </div>
  )
}

function BundleSectionHeader({ section }: { section: BundleSection }) {
  const tone = stateStringTone(section.state)
  return (
    <div className="mb-1.5 flex min-w-0 items-center gap-2">
      <span className={clsx("h-4 w-1.5 shrink-0 rounded-full", RAIL[tone])} />
      <TypeIcon type="Feature" />
      {section.code && (
        <span className="shrink-0 rounded bg-ink px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-white">
          {section.code}
        </span>
      )}
      <h2 className="truncate font-display text-sm font-bold text-ink">{section.title}</h2>
      {section.state && <StatePill state={section.state} />}
      <span className="num text-[11px] font-medium text-slate-400">{section.rows.length}</span>
      {section.assignees.length > 0 && (
        <span className="ml-auto flex shrink-0 flex-wrap items-center gap-1.5">
          {section.assignees.map((name) => (
            <span
              key={name}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-0.5 pr-2 text-[11px] text-slate-600 ring-1 ring-slate-200"
            >
              <Avatar name={name} />
              {name}
            </span>
          ))}
        </span>
      )}
    </div>
  )
}

function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
        {children}
      </span>
      <span className="h-px flex-1 bg-slate-200" />
    </div>
  )
}

function ViewToggle({ view, onChange }: { view: View; onChange: (v: View) => void }) {
  return (
    <div className="inline-flex shrink-0 rounded-lg border border-slate-200 bg-white/70 p-0.5 text-xs font-medium">
      {(
        [
          ["type", "By type"],
          ["bundle", "By bundle"],
        ] as [View, string][]
      ).map(([v, label]) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={clsx(
            "rounded-md px-3 py-1 transition-colors",
            view === v ? "bg-ink text-white shadow-sm" : "text-slate-500 hover:text-ink",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}
